import { useEffect, useState } from "react";
import { Plus, X, Pencil, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { NAV_CATEGORIES, CATEGORY_META, SUBMISSION_TYPES, SUBMISSION_STATUS_META } from "@/i18n/translations";

const inputCls = "w-full border border-tertiary bg-white px-3 py-2.5 text-sm outline-none transition-colors focus:border-champagne focus:ring-1 focus:ring-champagne";
const labelCls = "mb-1.5 block text-xs uppercase tracking-[0.12em] text-anthracite/50";
const EMPTY = { submission_type: "offer", category: "opportunities", lang: "fr", title: "", summary: "", description: "", sector: "", country: "", region: "", intent: "offer" };

export default function MemberSubmissions() {
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [tagsInput, setTagsInput] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/member/submissions").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditId(null); setForm({ ...EMPTY, lang }); setTagsInput(""); setOpen(true); };
  const openEdit = (s) => { setEditId(s.id); setForm({ ...EMPTY, ...s }); setTagsInput((s.tags || []).join(", ")); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, tags: tagsInput.split(",").map((t) => t.trim()).filter(Boolean) };
    try {
      if (editId) await api.put(`/member/submissions/${editId}`, payload);
      else await api.post("/member/submissions", payload);
      toast.success(lang === "fr" ? "Soumission envoyée à l'équipe RSTI" : "Submission sent to the RSTI team");
      setOpen(false); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="border border-tertiary bg-white p-6" data-testid="member-submissions">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-serif text-2xl text-anthracite">{lang === "fr" ? "Mes soumissions" : "My submissions"}</h2>
        <button onClick={openNew} data-testid="new-submission-btn" className="inline-flex items-center gap-2 bg-anthracite px-4 py-2 text-sm font-medium text-ivory hover:bg-black"><Plus className="h-4 w-4" /> {lang === "fr" ? "Publier" : "Submit"}</button>
      </div>
      <p className="mb-5 text-xs leading-relaxed text-anthracite/50">
        {lang === "fr"
          ? "Toute soumission est examinée et qualifiée par l'équipe RSTI avant une éventuelle publication. Elle n'est jamais publiée automatiquement."
          : "Every submission is reviewed and qualified by the RSTI team before any publication. It is never published automatically."}
      </p>

      {items.length === 0 ? (
        <p className="py-8 text-center text-sm text-anthracite/40">{lang === "fr" ? "Aucune soumission pour l'instant." : "No submissions yet."}</p>
      ) : (
        <div className="space-y-3">
          {items.map((s) => {
            const st = SUBMISSION_STATUS_META[s.status] || SUBMISSION_STATUS_META.pending;
            const canEdit = s.status === "pending" || s.status === "info_requested";
            return (
              <div key={s.id} data-testid={`submission-${s.id}`} className="border border-tertiary p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-anthracite">{s.title}</p>
                    <p className="text-xs text-anthracite/50">
                      {SUBMISSION_TYPES.find((t) => t.value === s.submission_type)?.[lang]} · {CATEGORY_META[s.category]?.[lang]}
                    </p>
                  </div>
                  <span className={`rounded-full border px-2.5 py-0.5 text-xs ${st.cls}`}>{st[lang]}</span>
                </div>
                {s.status === "info_requested" && s.info_request && (
                  <p className="mt-2 border-l-2 border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    <Clock className="mr-1 inline h-3 w-3" /> {s.info_request}
                  </p>
                )}
                {s.status === "rejected" && s.rejection_reason && (
                  <p className="mt-2 text-xs text-red-600">{s.rejection_reason}</p>
                )}
                {canEdit && (
                  <button onClick={() => openEdit(s)} className="mt-2 inline-flex items-center gap-1 text-xs text-champagne hover:underline"><Pencil className="h-3 w-3" /> {lang === "fr" ? "Modifier / compléter" : "Edit / complete"}</button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto bg-anthracite/50 p-4">
          <form onSubmit={submit} data-testid="submission-form" className="my-8 w-full max-w-2xl bg-white p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-serif text-xl text-anthracite">{editId ? (lang === "fr" ? "Modifier ma soumission" : "Edit submission") : (lang === "fr" ? "Nouvelle soumission" : "New submission")}</h3>
              <button type="button" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>{lang === "fr" ? "Type" : "Type"}</label>
                  <select value={form.submission_type} onChange={(e) => set("submission_type", e.target.value)} data-testid="submission-type" className={inputCls}>
                    {SUBMISSION_TYPES.map((t) => <option key={t.value} value={t.value}>{t[lang]}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>{lang === "fr" ? "Domaine" : "Domain"}</label>
                  <select value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>
                    {NAV_CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_META[c][lang]}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className={labelCls}>{lang === "fr" ? "Titre" : "Title"}</label>
                <input required value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="submission-title" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>{lang === "fr" ? "Résumé" : "Summary"}</label>
                <textarea rows={2} value={form.summary} onChange={(e) => set("summary", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>{lang === "fr" ? "Description détaillée" : "Detailed description"}</label>
                <textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} />
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <div><label className={labelCls}>{lang === "fr" ? "Secteur" : "Sector"}</label><input value={form.sector} onChange={(e) => set("sector", e.target.value)} className={inputCls} /></div>
                <div><label className={labelCls}>{lang === "fr" ? "Pays / marché" : "Country / market"}</label><input value={form.country} onChange={(e) => set("country", e.target.value)} className={inputCls} /></div>
                <div>
                  <label className={labelCls}>{lang === "fr" ? "Intention" : "Intent"}</label>
                  <select value={form.intent} onChange={(e) => set("intent", e.target.value)} className={inputCls}>
                    <option value="offer">{lang === "fr" ? "Propose" : "Offers"}</option>
                    <option value="seek">{lang === "fr" ? "Recherche" : "Seeks"}</option>
                    <option value="both">{lang === "fr" ? "Les deux" : "Both"}</option>
                  </select>
                </div>
              </div>
              <div><label className={labelCls}>{lang === "fr" ? "Mots-clés (virgules)" : "Keywords (commas)"}</label><input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} className={inputCls} /></div>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button type="button" onClick={() => setOpen(false)} className="border border-tertiary px-5 py-2.5 text-sm hover:bg-sand">{lang === "fr" ? "Annuler" : "Cancel"}</button>
              <button type="submit" disabled={saving} data-testid="submission-submit" className="bg-anthracite px-5 py-2.5 text-sm font-medium text-ivory hover:bg-black disabled:opacity-50">{saving ? "…" : (lang === "fr" ? "Envoyer à RSTI" : "Send to RSTI")}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
